import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {FormulaEngine} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/FormulaEngine';
import {OhmParseInputResult} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/OhmParseResult';
import {DesignStateFromJsonCtx} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignState';
import {
    CloudSheet,
    ICloudSheetSnapshot
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cloud/CloudSheet';
import {INamedRangeSnapshot, NamedRange} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/NamedRange';
import {ISheetSnapshot, Sheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Sheet';
import {ILinearFormulaToken} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/FormulaToken';
import {Slot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/Slot';
import {SlotUtil} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotUtil';
import {AnySheet, IAnySheetSnapshot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {SlotDependencies} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotTypes';
import {SlotGlobalKey} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {Workbook} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Workbook';
import {area, mk} from '@mintdata/mint-kit/api/log/mk';
import {NamedRangeId, SheetId} from '@mintdata/mint-kit/api/models/Identifiers';
import {ArrayUtils} from '@mintdata/mint-kit/impl/util/ArrayUtils';
import {Assert} from '@mintdata/mint-kit/impl/util/Assert';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import {action, computed, observable} from 'mobx';
import {createTransformer, ITransformer} from 'mobx-utils';

export interface ISpreadsheetRuntimeStateSnapshot
{
    sheets:IAnySheetSnapshot[];
    namedRanges:{[id:string]:INamedRangeSnapshot}
}

export class SpreadsheetRuntimeState
{
    @observable
    public sheets:AnySheet[];

    @observable.struct
    public slotDependencies:Map<SlotGlobalKey, SlotDependencies> = new Map();

    // There is no need to track dependencies for lazy slot in 'slotDependencies' map
    // because after dependencies changing that slot's formula should not be evaluated immediately.
    // But there is a need to track dependencies for lazy slot for recalculating it's formula after cut/paste
    // operation on it's dependencies or moving slots (insert/delete rows/cols)
    @observable
    public lazySlotDependencies:Map<SlotGlobalKey, Set<SlotGlobalKey>> = new Map();

    @observable
    public namedRanges:Array<NamedRange> = [];

    private constructor()
    {
    }

    public static create(workbook:Workbook):SpreadsheetRuntimeState
    {
        const spreadsheetRuntimeState:SpreadsheetRuntimeState = new SpreadsheetRuntimeState();

        spreadsheetRuntimeState.sheets = [
            Sheet.create(IdUtil.newGUID(), workbook, 'Sheet1'),
            Sheet.create(IdUtil.newGUID(), workbook, 'Sheet2'),
            Sheet.create(IdUtil.newGUID(), workbook, 'Sheet3')
        ];

        return spreadsheetRuntimeState;
    }

    @computed
    public get sheetsMap():Map<SheetId, AnySheet>
    {
        const sheetsMap:Map<SheetId, AnySheet> = new Map<SheetId, AnySheet>();

        this.sheets.forEach((sheet:AnySheet) => sheetsMap.set(sheet.id, sheet));

        return sheetsMap;
    }

    @computed
    public get namedRangesIdMap():Map<NamedRangeId, NamedRange>
    {
        const namedRangesIdMap:Map<NamedRangeId, NamedRange> = new Map<NamedRangeId, NamedRange>();

        this.namedRanges.forEach(range =>
            namedRangesIdMap.set(range.id, range)
        );

        return namedRangesIdMap;
    }

    @computed
    public get namedRangesNameMap():Map<string, NamedRange>
    {
        const namedRangesNameMap:Map<string, NamedRange> = new Map<string, NamedRange>();

        this.namedRanges.forEach(range =>
            range.name && namedRangesNameMap.set(range.name, range)
        );

        return namedRangesNameMap;
    }

    @action
    public insertSheet(insertIndex:number, sheet:AnySheet):void
    {
        this.sheets.splice(insertIndex, 0, sheet);
    }

    @action
    public renameSheet(sheetId:SheetId, newName:string):void
    {
        const {formulaInput} = commonControllers.spreadsheets;

        const sheetIndex:number = this.sheets.findIndex((sheet:AnySheet) => sheet.id === sheetId);

        if( this.sheets[sheetIndex].name === newName )
            return;

        let oldSheetName = this.sheets[sheetIndex].name;
        let newSheetName = newName;

        if( sheetIndex >= 0 )
        {
            this.sheets.forEach((sheet: Sheet) =>
            {
                sheet.slots.forEach((slot: Slot) =>
                {
                    if( SlotUtil.isFormula(slot.formula) && slot.formula.toString().includes(oldSheetName.replace(/'/g, '\'\'')) )
                    {
                        const ohmResult:OhmParseInputResult = FormulaEngine.getInstance().parseInputRC(slot.formula, slot.location);
                        
                        const formulaTokens:ILinearFormulaToken[] = formulaInput.getPlainFormulaTokens(ohmResult.result);
                        const updatedFormulaTokens:ILinearFormulaToken[] = formulaInput
                                .replaceSheetNameInExternalCellRefs(formulaTokens, oldSheetName, newSheetName);

                        const updatedFormulaText = formulaInput.getFormulaTokensText(updatedFormulaTokens, slot.location.colIndex, slot.location.rowIndex, false);

                        slot.setFormula(updatedFormulaText);
                    }
                });
            });

            this.sheets[sheetIndex].name = newName;
        }
        else
            mk.error(area.SheetController, 'Sheet is not found. SheetId:', sheetId);
    }

    @action
    public moveSheet(newIndex:number, sheetId:SheetId):void
    {
        const sheetIndex:number = this.sheets.findIndex((sheet:AnySheet) => sheet.id === sheetId);

        if( sheetIndex >= 0 )
        {
            const sheetsCopy = this.sheets.slice();
            const spliceFunc = Array.prototype.splice.bind(sheetsCopy);
            spliceFunc(newIndex, 0, spliceFunc(sheetIndex, 1)[0]);
            this.sheets = sheetsCopy;
        }
        else
            mk.error(area.SheetController, 'Sheet is not found. SheetId:', sheetId);
    }

    @action
    public deleteSheet(sheetId:SheetId):void
    {
        const sheetIndex:number = this.sheets.findIndex((sheet:AnySheet) => sheet.id === sheetId);

        if( sheetIndex >= 0 )
            this.sheets.splice(sheetIndex, 1);
        else
            mk.error(area.SheetController, 'Sheet is not found. SheetId:', sheetId);
    }

    @action
    public setSlotDependencies(formulaSlot:SlotGlobalKey, dependencies:SlotDependencies):void
    {
        this.slotDependencies.set(formulaSlot, dependencies);
    }

    @action
    public addNamedRange(range:NamedRange):void
    {
        if( range.name && this.namedRanges.find(existing => existing.name === range.name) )
        {
            mk.error(area.SheetController, `NamedRange with name "${range.name}" already exists`);
            return;
        }

        //this shouldn't happen
        Assert.no(this.namedRanges.find(existing => existing.id === range.id));

        this.namedRanges.push(range);
    }

    @action
    public deleteNamedRange(rangeId:NamedRangeId):NamedRange
    {
        const rangeIndex = this.namedRanges.findIndex(range => range.id === rangeId);

        if( rangeIndex < 0 )
        {
            mk.error(area.SheetController, 'Range is not found. RangeId:', rangeId);
            return null;
        }

        const range = this.namedRanges[rangeIndex];
        this.namedRanges.splice(rangeIndex, 1);
        return range;
    }

    @action
    public getNameRangeByName(name:string):NamedRange | null
    {
        return this.namedRangesNameMap.get(name);
    }

    @action
    public getNameRangeById(id:NamedRangeId):NamedRange | null
    {
        return this.namedRangesIdMap.get(id);
    }

    public static toJson:ITransformer<SpreadsheetRuntimeState, ISpreadsheetRuntimeStateSnapshot> = createTransformer((spreadsheetRuntimeState:SpreadsheetRuntimeState) =>
    {
        return {
            sheets: spreadsheetRuntimeState.sheets.map((sheet:AnySheet) => (
                sheet.isCloud ? CloudSheet.toJson(sheet as CloudSheet) : Sheet.toJson(sheet as Sheet)
            )),
            namedRanges: ArrayUtils.arrayToMap(spreadsheetRuntimeState.namedRanges,
                r => r.id,
                r => NamedRange.toJson(r)
            )
        };
    });

    public static fromJson(json:ISpreadsheetRuntimeStateSnapshot,
                           workbook:Workbook,
                           currentState?:SpreadsheetRuntimeState,
                           currentJson?:ISpreadsheetRuntimeStateSnapshot,
                           ctx?:DesignStateFromJsonCtx):SpreadsheetRuntimeState
    {
        if( !currentState )
            return SpreadsheetRuntimeState.fromJsonDeep(json, workbook);

        if( !currentJson )
            currentJson = SpreadsheetRuntimeState.toJson(currentState);

        const spreadsheetRuntimeState:SpreadsheetRuntimeState = new SpreadsheetRuntimeState();

        if( json.sheets === currentJson.sheets )
        {
            spreadsheetRuntimeState.sheets = currentState.sheets;
            spreadsheetRuntimeState.sheets.forEach((sheet:AnySheet) => sheet.workbook = workbook);
        }
        else
        {
            spreadsheetRuntimeState.sheets = json.sheets.map((sheet:IAnySheetSnapshot, index:number) =>
            {
                const sheetCurrentState:AnySheet = currentState.sheets[index];
                const sheetCurrentJson:IAnySheetSnapshot = currentJson.sheets[index];

                if( json.sheets[index] === currentJson.sheets[index] )
                {
                    currentState.sheets[index].workbook = workbook;
                    return currentState.sheets[index];
                }

                return ((sheet as ISheetSnapshot).slots !== undefined)
                    ? Sheet.fromJson(json.sheets[index] as ISheetSnapshot, workbook, sheetCurrentState as Sheet, sheetCurrentJson as ISheetSnapshot, ctx)
                    : CloudSheet.fromJson(json.sheets[index] as ICloudSheetSnapshot, workbook, sheetCurrentState as CloudSheet, sheetCurrentJson as ICloudSheetSnapshot, ctx);
            });

            if( ctx )
            {
                const addedSheets = spreadsheetRuntimeState.sheets.filter((target:AnySheet) => (
                    !currentState.sheets.some((sheet:AnySheet) => sheet.id === target.id)
                ));

                const removedSheets = currentState.sheets.filter((target:AnySheet) => (
                    !spreadsheetRuntimeState.sheets.some((sheet:AnySheet) => sheet.id === target.id)
                ));

                ctx.addedSheets.push(...addedSheets);
                ctx.removedSheets.push(...removedSheets);
            }
        }

        if( json.namedRanges === currentJson.namedRanges )
        {
            spreadsheetRuntimeState.namedRanges = currentState.namedRanges;
        }
        else
        {
            spreadsheetRuntimeState.namedRanges = Object.entries(json.namedRanges).map(([rangeId, rangeJson]) =>
            {
                const currentRangeJson = currentJson.namedRanges[rangeId] as INamedRangeSnapshot;

                const currentRangeState = currentState.namedRangesIdMap.get(rangeId);

                return rangeJson === currentRangeJson
                    ? currentRangeState
                    : NamedRange.fromJson(rangeJson, currentRangeState, currentRangeJson);
            });
        }

        SpreadsheetRuntimeState._restoreUiState(spreadsheetRuntimeState, currentState);

        return spreadsheetRuntimeState;
    }

    public static fromJsonDeep(json:ISpreadsheetRuntimeStateSnapshot, workbook:Workbook):SpreadsheetRuntimeState
    {
        const spreadsheetRuntimeState:SpreadsheetRuntimeState = new SpreadsheetRuntimeState();

        spreadsheetRuntimeState.sheets = json.sheets.map((sheet:IAnySheetSnapshot, index:number) => (
            ((sheet as ISheetSnapshot).slots !== undefined)
                ? Sheet.fromJsonDeep(json.sheets[index] as ISheetSnapshot, workbook)
                : CloudSheet.fromJsonDeep(json.sheets[index] as ICloudSheetSnapshot, workbook)
        ));

        spreadsheetRuntimeState.namedRanges = Object.entries(json.namedRanges)
            .map(([rangeId, rangeJson]) => NamedRange.fromJsonDeep(rangeJson));

        return spreadsheetRuntimeState;
    }

    private static _restoreUiState(state:SpreadsheetRuntimeState, currentState:SpreadsheetRuntimeState):void
    {
        state.slotDependencies = currentState.slotDependencies;
        state.lazySlotDependencies = currentState.lazySlotDependencies;
    }
}
